import { changeDpiDataUrl } from "changedpi";
import {
  renderPolotnoExport,
  renderBatchExports,
  checkRenderStatus,
  downloadRenderedFile,
  updateExportProgress,
  updateDownloadProgress,
} from "../../../redux/slices/PolotnoSlice";

// Helper function for synchronous render completion (no polling needed)
export const waitForRenderCompletion = async (
  jobId,
  dispatch,
  maxWaitTime = 300000
) => {
  // For synchronous requests, the job should already be completed
  // This function is kept for backward compatibility but doesn't need to poll
  console.log(`Synchronous render completed for job ${jobId}`);
  return { status: "done", id: jobId };
};

function convertImageToDataUrl(imageBlob, format = "image/png") {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      const newDataUrl = canvas.toDataURL(format);
      resolve(newDataUrl);
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(imageBlob);
  });
}

// Single file export using cloud render (synchronous)
export const exportSingleFile = async (
  store,
  pageId,
  options,
  dispatch,
  fonts = []
) => {
  const {
    format = "jpeg",
    dpi = 300,
    quality = 0.9,
    pixelRatio,
    filename = "export",
  } = options;

  try {
    const page = store.pages.find((p) => p.id === pageId);
    if (!page) throw new Error("Page not found");

    const designJson = {
      ...store.toJSON(),
      pages: [page.toJSON()],
    };

    // Submit synchronous render job
    const renderResult = await dispatch(
      renderPolotnoExport({
        designJson,
        format,
        dpi,
        quality,
        pixelRatio,
        fonts: designJson?.fonts || [],
      })
    ).unwrap();

    // Render must finish instantly
    if (renderResult.status !== "done" || !renderResult.output) {
      throw new Error("Synchronous render failed");
    }

    // Fetch the output directly from Polotno Cloud Render
    const res = await fetch(renderResult.output);
    if (!res.ok) throw new Error("Failed to download output file");

    const blob = await res.blob();
    const url = URL.createObjectURL(blob);

    // Trigger download
    const link = document.createElement("a");
    link.href = url;
    link.download = `${filename}.${format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
    return true;

  } catch (error) {
    console.error("Cloud render export error:", error);
    throw error;
  }
};

// Batch export using cloud render (synchronous)
export const exportBatchFiles = async (
  store,
  pages,
  options,
  dispatch,
  onProgress,
  fonts = []
) => {
  const {
    format = "jpeg",
    dpi = 300,
    quality = 0.9,
    pixelRatio=1,
    batchSize = 34,
  } = options;

  try {
    const results = [];
    const totalPages = pages.length;
    let processedCount = 0;

    // Process in batches to avoid overwhelming the API
    for (let i = 0; i < totalPages; i += batchSize) {
      const batch = pages.slice(i, i + batchSize);
      const batchDesigns = [];

      // Prepare designs for this batch
      for (const page of batch) {
        const designJson = {
          ...store.toJSON(),
          pages: [page.toJSON()],
        };

        // Prepare design for cloud rendering (handle fonts)
        batchDesigns.push(designJson);
      }

      // Submit batch render jobs (synchronous)
      const batchJobs = await dispatch(
        renderBatchExports({
          designs: batchDesigns,
          format,
          dpi,
          quality,
          pixelRatio,
          fonts,
        })
      ).unwrap();

      // For synchronous requests, jobs should be done immediately
      for (const job of batchJobs) {
        try {
          if (job.status === "done" && job.output) {
            results.push(job);
            processedCount++;

            // Update progress
            if (onProgress) {
              onProgress(processedCount, totalPages);
            }
          } else {
            console.error(`Synchronous render failed for job ${job.id}`);
          }
        } catch (error) {
          console.error(`Error processing job ${job.id}:`, error);
          // Continue with other jobs even if one fails
        }
      }

      // Small delay between batches
      if (i + batchSize < totalPages) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    }

    return results;
  } catch (error) {
    console.error("Cloud render batch export error:", error);
    throw error;
  }
};

// Download all completed renders as a zip (synchronous)
export const downloadBatchAsZip = async (
  renderJobs,
  filenames,
  selectedDpi,
  selectedFormat
) => {
  try {
    const JSZip = (await import("jszip")).default;
    const zip = new JSZip();
    const folder = zip.folder("images");

    const jobsToDownload = renderJobs.map((job, i) => {
      const filename = filenames[i] || `export_${i + 1}`;

      if (job.status === "done" && job.output) {
        return (async () => {
          try {
            const res = await fetch(job.output);
            if (!res.ok) throw new Error(`Failed fetch: ${res.status}`);

            const blob = await res.blob();

            folder.file(
              `${filename}.${selectedFormat}`,
              blob
            );
          } catch (err) {
            console.error(`Error downloading job ${job.id}:`, err);
          }
        })();
      } else {
        return Promise.resolve();
      }
    });

    await Promise.all(jobsToDownload);

    const content = await zip.generateAsync({
      type: "blob",
      compression: "DEFLATE",
      compressionOptions: { level: 4 },
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(content);
    link.download = selectedFormat === "pdf" ? "All_PDFs.zip" : "All_Images.zip";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);

    return true;
  } catch (error) {
    console.error("Error creating zip:", error);
    throw error;
  }
};


// Helper to get quality setting based on format
export const getQualityForFormat = (format) => {
  switch (format) {
    case "png":
      return 0.5;
    case "jpeg":
    case "pdf":
    default:
      return 0.9;
  }
};

// Helper to validate export options
export const validateExportOptions = (options) => {
  const { format, dpi, quality } = options;

  if (!["jpeg", "png", "pdf"].includes(format)) {
    throw new Error("Invalid format. Must be jpeg, png, or pdf");
  }

  if (dpi && ![72, 150, 300].includes(parseInt(dpi))) {
    throw new Error("Invalid DPI. Must be 72, 150, or 300");
  }

  if (quality && (quality < 0 || quality > 1)) {
    throw new Error("Quality must be between 0 and 1");
  }

  return true;
};
